/**
 * 模块: DataCheck主组件
 * 职责: 数据核对页面主入口
 * 限制: 文件<280行, 函数<30行
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Typography, Card, App } from 'antd';
import type { TablePaginationConfig } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { DataRow, SKU } from './types';
import { calculateStats } from './utils';
import { createColumns } from './columns';
import StatsCard from './StatsCard';
import SKUFilter from './SKUFilter';
import '../DataCheck.css';

const { Title } = Typography;

const DataCheck: React.FC = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DataRow[]>([]);
  const [skus, setSkus] = useState<SKU[]>([]);
  const [selectedSKU, setSelectedSKU] = useState<number | undefined>();
  const [pageSize, setPageSize] = useState(50);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedSKU ? { sku_id: selectedSKU } : {};
      const response = await api.get('/skus/data_check/', { params });
      setData(response.data.data);
      setSkus(response.data.skus);
    } catch (error: any) {
      console.error('获取数据失败:', error);
      message.error(`数据加载失败: ${error.message || '后端API不可用'}`);
      setData([]); // 设置空数据避免卡住
      setSkus([]); // 设置空SKU列表
    } finally {
      setLoading(false);
    }
  }, [selectedSKU, message]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }
  };

  const stats = calculateStats(data);
  const columns = createColumns(skus, data);

  return (
    <div className="data-check-container">
      <div className="data-check-header">
        <Title level={2}>
          <CheckCircleOutlined /> 数据核对
        </Title>
        <p>查看和核对所有周期的详细数据</p>
      </div>

      <StatsCard stats={stats} />

      <Card>
        <SKUFilter
          skus={skus}
          selectedSKU={selectedSKU}
          onChange={setSelectedSKU}
        />

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 2400, y: 600 }}
          onChange={handleTableChange}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default DataCheck;

